import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { SignUpUserInfoPage } from '../SignUpUserInfoPage/SignUpUserInfoPage';
import { isSignupDemo, resolveSignupVariant } from '../../utils/signupExperiment';
import { trackSignupVariantExposure } from './onboardingV2.analytics';
import SignUpV2Flow from './SignUpV2Flow';
import { rememberSourceFromUrl } from '../../utils/acquisitionSource';

/**
 * `/sign-up` entry: renders the untouched v1 page (control) or signup v2.
 * Demo visits (`?signup=v2&demo=1`) never persist the variant, so a demo cannot turn a browser
 * into a real v2 signup afterwards.
 */
const SignUpEntry = (props) => {
    const { search } = useLocation();
    const isDemo = isSignupDemo(search);
    // Re-resolved when the query changes (v2 steps live in `?step=`); the stored assignment keeps
    // the variant and its original source stable across steps.
    const assignment = useMemo(() => {
        const forced = new URLSearchParams(search).get('signup');
        return isSignupDemo(search) && forced === 'v2'
            ? { variant: 'v2', source: 'query', isNew: false }
            : resolveSignupVariant(search);
    }, [search]);

    // `/sign-up?source=IG` attributes the signup like the other entry pages do.
    useEffect(() => {
        rememberSourceFromUrl();
    }, []);

    // Exposure is reported once per page load and variant, not on every step change.
    const reportedVariantRef = useRef(null);
    useEffect(() => {
        if (reportedVariantRef.current === assignment.variant) return;
        reportedVariantRef.current = assignment.variant;
        trackSignupVariantExposure(assignment, isDemo);
    }, [assignment, isDemo]);

    if (assignment.variant === 'v2') return <SignUpV2Flow mode="signup" />;
    return <SignUpUserInfoPage {...props} signupVariant="v1" />;
};

export default SignUpEntry;
